
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTasks } from '@/context/TaskContext';
import { useTimerContext } from '@/context/TimerContext';
import { useBackgroundBrightness } from '@/hooks/useBackgroundBrightness';
import { useUnsplashBackground } from '@/hooks/useUnsplashBackground';
import { useActiveTimeEntry } from '@/hooks/timer/useActiveTimeEntry';
import SettingsModal from './SettingsModal';
import NavigationBar from './NavigationBar';
import TimerClock from './timer/TimerClock';
import TimerHeader from './timer/TimerHeader';
import FocusAreaCard from './FocusAreaCard';
import { useAuth } from '@/context/AuthContext';

const PomodoroTimer: React.FC = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { resetTasks } = useTasks();
  const { session } = useAuth();
  const { timer } = useTimerContext();
  const { finalizeCurrentEntry } = useActiveTimeEntry();
  const { backgroundInfo } = useUnsplashBackground();
  const { isLight } = useBackgroundBrightness(backgroundInfo);
  
  const [showSettings, setShowSettings] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const getTimerSize = () => {
    if (isMobile) {
      return Math.min(window.innerWidth - 40, 350);
    }
    return 525;
  };
  
  const timerSize = getTimerSize();

  const handleToggleTimer = () => {
    timer.toggleTimer();
  };

  const handlePrevious = () => {
    timer.goToPrevious();
  };

  const handleNext = () => {
    timer.goToNext();
  };

  // Reset tasks when user logs out
  useEffect(() => {
    if (!session) {
      resetTasks();
      if (finalizeCurrentEntry) {
        finalizeCurrentEntry('interrupted').catch(error => {
          console.error("Error finalizing session on logout:", error);
        });
      }
    }
  }, [session, resetTasks, finalizeCurrentEntry]);

  return (
    <div className="min-h-[100vh] flex flex-col">
      <NavigationBar 
        onOpenSettings={() => setShowSettings(true)}
        onOpenTaskList={() => navigate('/tasks')}
        isLightBackground={isLight}
      />

      <TimerHeader currentTime={currentTime} />

      <div className="flex-1 flex flex-col items-center justify-center px-2 pt-4 sm:pt-8 pb-16">
        <TimerClock 
          progress={timer.progress}
          minutes={timer.minutes}
          seconds={timer.seconds}
          isRunning={timer.isRunning}
          timerTitle={timer.timerTitle}
          isMobile={isMobile}
          timerSize={timerSize}
          onTogglePlay={handleToggleTimer}
          onPrevious={handlePrevious}
          onNext={handleNext}
          timerType={timer.timerType}
          onChangeTimerType={timer.changeTimerType}
        />

        <div className="mt-6">
          <FocusAreaCard 
            sessions={timer.settings.sessions}
            currentSession={timer.currentSessionIndex}
            currentType={timer.timerType}
            isMobile={isMobile}
            activeIndex={timer.sequencePosition}
            onOpenTaskList={() => navigate('/tasks')}
            currentTime={currentTime}
            timerSize={timerSize}
          />
        </div>
      </div>

      <SettingsModal 
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        settings={timer.settings}
        onSave={timer.saveSettings}
      />
    </div>
  );
};

export default PomodoroTimer;
